import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import { useThemeStore } from './stores/themeStore'
import Layout from './layouts/Layout'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import Dashboard from './pages/Dashboard'
import Products from './pages/inventory/Products'
import ProductWizard from './pages/inventory/ProductWizard'
import ProductDetail from './pages/inventory/ProductDetail'
import StockReceive from './pages/inventory/StockReceive'
import LowStock from './pages/inventory/LowStock'
import StockTransfers from './pages/inventory/StockTransfers'
import StockAdjustments from './pages/inventory/StockAdjustments'
import CycleCounts from './pages/inventory/CycleCounts'
import PurchaseOrders from './pages/purchase/PurchaseOrders'
import Suppliers from './pages/purchase/Suppliers'
import Bills from './pages/purchase/Bills'
import POS from './pages/pos/POS'
import Warehouses from './pages/inventory/Warehouses'
import Categories from './pages/products/Categories'
import Brands from './pages/products/Brands'
import Units from './pages/products/Units'
import Stock from './pages/inventory/Stock'
import Movements from './pages/inventory/Movements'
import AdminUsers from './pages/admin/Users'
import AdminRoles from './pages/admin/Roles'
import Payments from './pages/sales/Payments'
import Receipts from './pages/pos/Receipts'
import Shifts from './pages/pos/Shifts'
import Settings from './pages/Settings'
import Customers from './pages/customers/Customers'
import SalesReports from './pages/sales/SalesReports'
import SalesHistory from './pages/sales/SalesHistory'
import NotFound from './pages/NotFound'

import { PermissionGuard } from './components/PermissionGuard'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    )
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    )
  }

  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>
}

function App() {
  const { initializeTheme } = useThemeStore()
  const { initializeAuth } = useAuthStore()

  // Initialize on mount
  React.useEffect(() => {
    initializeTheme()
    initializeAuth()
  }, [initializeTheme, initializeAuth])

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

      {/* Private Routes */}
      <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route path="/" element={<PermissionGuard permission="dashboard.read"><Dashboard /></PermissionGuard>} />
        <Route path="/inventory/products" element={<PermissionGuard permission="product.read"><Products /></PermissionGuard>} />
        <Route path="/inventory/products/new" element={<PermissionGuard permission="product.write"><ProductWizard /></PermissionGuard>} />
        <Route path="/inventory/products/:id" element={<PermissionGuard permission="product.read"><ProductDetail /></PermissionGuard>} />
        <Route path="/inventory/receive" element={<PermissionGuard permission="stock.receive"><StockReceive /></PermissionGuard>} />
        <Route path="/inventory/transfers" element={<PermissionGuard permission="stock.transfer"><StockTransfers /></PermissionGuard>} />
        <Route path="/inventory/adjustments" element={<PermissionGuard permission="stock.adjust"><StockAdjustments /></PermissionGuard>} />
        <Route path="/inventory/cycle-counts" element={<PermissionGuard permission="stock.count"><CycleCounts /></PermissionGuard>} />
        <Route path="/inventory/warehouses" element={<PermissionGuard permission="stock.read"><Warehouses /></PermissionGuard>} />
        <Route path="/inventory/low-stock" element={<PermissionGuard permission="stock.read"><LowStock /></PermissionGuard>} />
        <Route path="/purchase/orders" element={<PermissionGuard permission="purchase.read"><PurchaseOrders /></PermissionGuard>} />
        <Route path="/purchase/suppliers" element={<PermissionGuard permission="supplier.read"><Suppliers /></PermissionGuard>} />
        <Route path="/purchase/bills" element={<PermissionGuard permission="bill.read"><Bills /></PermissionGuard>} />
        <Route path="/pos" element={<PermissionGuard permission="pos.sell"><POS /></PermissionGuard>} />
        <Route path="/sales" element={<PermissionGuard permission="sales.read"><SalesHistory /></PermissionGuard>} />
        <Route path="/sales/reports" element={<PermissionGuard permission="sales.report"><SalesReports /></PermissionGuard>} />
        <Route path="/customers" element={<PermissionGuard permission="customer.read"><Customers /></PermissionGuard>} />
        <Route path="/settings" element={<PermissionGuard permission="settings.manage"><Settings /></PermissionGuard>} />
        <Route path="/products/categories" element={<PermissionGuard permission="product.read"><Categories /></PermissionGuard>} />
        <Route path="/products/brands" element={<PermissionGuard permission="product.read"><Brands /></PermissionGuard>} />
        <Route path="/products/units" element={<PermissionGuard permission="product.read"><Units /></PermissionGuard>} />
        <Route path="/inventory/stock" element={<PermissionGuard permission="stock.read"><Stock /></PermissionGuard>} />
        <Route path="/inventory/movements" element={<PermissionGuard permission="stock.read"><Movements /></PermissionGuard>} />
        <Route path="/admin/users" element={<PermissionGuard permission="admin.users"><AdminUsers /></PermissionGuard>} />
        <Route path="/admin/roles" element={<PermissionGuard permission="admin.roles"><AdminRoles /></PermissionGuard>} />
        <Route path="/sales/payments" element={<PermissionGuard permission="pos.payment"><Payments /></PermissionGuard>} />
        <Route path="/pos/receipts" element={<PermissionGuard permission="pos.receipts"><Receipts /></PermissionGuard>} />
        <Route path="/pos/shifts" element={<PermissionGuard permission="pos.shift"><Shifts /></PermissionGuard>} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default App